'use client';

/**
 * MarkdownRenderer Component
 *
 * A core component for rendering markdown content with:
 * - Proper markdown-to-HTML parsing using marked
 * - Security through DOMPurify sanitization
 * - Support for multiple content formats (Markdown, HTML, MDX)
 * - Accessible heading IDs for table of contents
 * - Beautiful typography with customizable styles
 *
 * Usage:
 * ```tsx
 * <MarkdownRenderer
 *   content={post.content}
 *   contentFormat={post.contentFormat}
 *   className="prose prose-lg"
 * />
 * ```
 */

import { useMemo } from 'react';
import { marked } from 'marked';
import { sanitizeHTML } from './sanitize-html';
import { cn } from './cn';

/**
 * Content format types supported by the API
 */
export type ContentFormat =
  | 'MARKDOWN' // Standard Markdown
  | 'MDX' // MDX (Markdown with JSX) - rendered as Markdown
  | 'HTML' // Raw HTML
  | 'PROSEMIRROR_JSON'; // ProseMirror JSON (not supported, returns empty)

/**
 * Theme variant for prose styling
 */
export type MarkdownTheme =
  | 'default' // Neutral gray styling
  | 'allys' // Navy/gold Allys branding
  | 'stone' // Minimal, greyscale Stone Investment
  | 'route-royal' // Professional blue Route Royal
  | 'custom'; // Use className for custom styling

export interface MarkdownRendererProps {
  /** The raw content to render */
  content: string;
  /** Format of the content (defaults to MARKDOWN) */
  contentFormat?: ContentFormat | undefined;
  /** Theme variant for prose styling */
  theme?: MarkdownTheme | undefined;
  /** Additional CSS classes */
  className?: string | undefined;
  /** Whether to add IDs to headings for TOC linking */
  addHeadingIds?: boolean | undefined;
  /** Custom styles for the container */
  style?: React.CSSProperties | undefined;
}

/**
 * Custom renderer for marked with heading IDs
 */
const renderer = {
  heading({ text, depth }: { text: string; depth: number }): string {
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return `<h${depth} id="${id}">${text}</h${depth}>\n`;
  },
};

// Configure marked with custom renderer
marked.use({
  renderer,
  gfm: true,
  breaks: true,
});

/**
 * Detect if content looks like Markdown text (vs JSON or HTML)
 */
function looksLikeMarkdown(content: string): boolean {
  if (!content) return false;

  // Check if it starts with JSON (ProseMirror format starts with { or [)
  const trimmed = content.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    // Try to parse as JSON - if it works, it's not markdown
    try {
      JSON.parse(trimmed);
      return false; // Valid JSON, not markdown
    } catch {
      // Not valid JSON, might be markdown with special chars
      return true;
    }
  }

  // Check for markdown indicators
  const markdownPatterns = [
    /^#{1,6}\s/m, // Headers
    /\*\*[^*]+\*\*/, // Bold
    /\*[^*]+\*/, // Italic
    /^\s*[-*+]\s/m, // Unordered lists
    /^\s*\d+\.\s/m, // Ordered lists
    /\[.+\]\(.+\)/, // Links
    /```[\s\S]*```/, // Code blocks
    /^\s*>/m, // Blockquotes
  ];

  return markdownPatterns.some(pattern => pattern.test(content));
}

/**
 * Detect if content looks like HTML
 */
function looksLikeHTML(content: string): boolean {
  if (!content) return false;
  const trimmed = content.trim();
  // Check for HTML tags
  return /<[a-z][\s\S]*>/i.test(trimmed);
}

/**
 * Wrap <table> elements in a responsive overflow container.
 * This ensures tables don't break the layout on mobile devices.
 */
function wrapTablesInOverflowContainer(html: string): string {
  return html
    .replace(/<table(\s|>)/g, '<div class="table-overflow"><table$1')
    .replace(/<\/table>/g, '</table></div>');
}

/**
 * Parse content based on its format
 */
function parseContent(content: string, format: ContentFormat): string {
  if (!content) return '';

  // Smart detection: detect actual content type regardless of declared format
  // This handles cases where the API returns wrong format metadata, or where
  // HTML content is passed with MARKDOWN format (e.g., static policy pages)
  let effectiveFormat = format;

  // Smart detection: handle mismatched format metadata
  if (
    format === 'PROSEMIRROR_JSON' ||
    format === 'MARKDOWN' ||
    format === 'MDX'
  ) {
    // First check if it's HTML - HTML takes precedence over markdown detection
    // because policy pages etc. often have full HTML content declared as MARKDOWN
    if (looksLikeHTML(content) && !looksLikeMarkdown(content)) {
      // Content has HTML tags but no markdown patterns - treat as HTML
      effectiveFormat = 'HTML';
    } else if (format === 'PROSEMIRROR_JSON' && looksLikeMarkdown(content)) {
      effectiveFormat = 'MARKDOWN';
    } else if (format === 'PROSEMIRROR_JSON' && looksLikeHTML(content)) {
      effectiveFormat = 'HTML';
    }
  }

  let parsed: string;

  switch (effectiveFormat) {
    case 'HTML':
      // Already HTML, just wrap tables
      parsed = content;
      break;

    case 'MARKDOWN':
    case 'MDX':
      // Parse markdown to HTML (synchronously)
      try {
        const result = marked.parse(content, { async: false });
        parsed = result as string;
      } catch (error) {
        console.error('[MarkdownRenderer] Failed to parse markdown:', error);
        parsed = content; // Fallback to raw content
      }
      break;

    case 'PROSEMIRROR_JSON':
      // Actual ProseMirror JSON - try to extract text content
      try {
        const json = JSON.parse(content);
        // Simple extraction of text content from ProseMirror JSON
        const extractText = (node: unknown): string => {
          if (!node || typeof node !== 'object') return '';
          const n = node as Record<string, unknown>;
          if (n.text && typeof n.text === 'string') return n.text;
          if (Array.isArray(n.content)) {
            return n.content.map(extractText).join('\n');
          }
          return '';
        };
        const text = extractText(json);
        if (text) {
          // Parse the extracted text as markdown
          const result = marked.parse(text, { async: false });
          parsed = result as string;
        } else {
          parsed = '';
        }
      } catch {
        // If JSON parsing fails, the content might actually be markdown/text
        try {
          const result = marked.parse(content, { async: false });
          parsed = result as string;
        } catch {
          parsed = content;
        }
      }
      break;

    default:
      // Default to markdown parsing for unknown formats (synchronously)
      try {
        const result = marked.parse(content, { async: false });
        parsed = result as string;
      } catch {
        parsed = content;
      }
      break;
  }

  // Post-process: wrap tables in responsive overflow containers
  return wrapTablesInOverflowContainer(parsed);
}

/**
 * Get theme-specific prose classes
 */
function getThemeClasses(theme: MarkdownTheme): string {
  switch (theme) {
    case 'allys':
      return `
        prose prose-lg max-w-none
        prose-headings:font-bold
        prose-headings:text-[#243462]
        prose-h1:text-4xl prose-h1:mb-6 prose-h1:mt-0
        prose-h2:text-3xl prose-h2:mb-4 prose-h2:mt-10 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
        prose-h3:text-2xl prose-h3:mb-3 prose-h3:mt-8
        prose-h4:text-xl prose-h4:mb-2 prose-h4:mt-6
        prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
        prose-a:text-[#BBA339] prose-a:font-medium prose-a:no-underline hover:prose-a:underline
        prose-strong:text-[#243462] prose-strong:font-bold
        prose-em:italic
        prose-blockquote:border-l-[#BBA339] prose-blockquote:border-l-4 prose-blockquote:bg-[#BBA339]/5
        prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:my-6 prose-blockquote:rounded-r-lg
        prose-blockquote:text-gray-700 prose-blockquote:not-italic
        prose-code:text-[#243462] prose-code:bg-[#BBA339]/10 prose-code:px-1.5 prose-code:py-0.5
        prose-code:rounded prose-code:font-medium prose-code:before:content-none prose-code:after:content-none
        prose-pre:bg-[#243462] prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:my-6
        prose-img:rounded-xl prose-img:my-6
        prose-ul:list-disc prose-ul:pl-6 prose-ul:my-4
        prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-4
        prose-li:text-gray-700 prose-li:my-1 prose-li:leading-relaxed prose-li:marker:text-[#BBA339]
        prose-hr:border-gray-200 prose-hr:my-8
        prose-table:w-full prose-table:border-collapse prose-table:my-6
        prose-thead:border-b-2 prose-thead:border-[#243462]/20
        prose-th:bg-[#243462]/5 prose-th:p-3 prose-th:text-left prose-th:font-semibold prose-th:text-[#243462] prose-th:border prose-th:border-gray-200
        prose-td:p-3 prose-td:border prose-td:border-gray-200 prose-td:text-gray-700
        prose-headings:scroll-mt-24
        [&>p+p]:mt-6
      `;

    case 'stone':
      return `
        prose prose-lg max-w-none
        prose-headings:font-normal prose-headings:text-[var(--color-text)]
        prose-h1:text-4xl prose-h1:mb-6 prose-h1:mt-0
        prose-h2:text-3xl prose-h2:mb-4 prose-h2:mt-10
        prose-h3:text-2xl prose-h3:mb-3 prose-h3:mt-8
        prose-h4:text-xl prose-h4:mb-2 prose-h4:mt-6
        prose-p:text-[var(--color-text-muted)] prose-p:leading-relaxed prose-p:mb-6
        prose-a:text-[var(--color-text)] prose-a:underline prose-a:underline-offset-4
        prose-strong:text-[var(--color-text)] prose-strong:font-medium
        prose-em:italic
        prose-blockquote:border-l-[var(--color-text)] prose-blockquote:border-l
        prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:my-6
        prose-blockquote:text-[var(--color-text-muted)] prose-blockquote:not-italic
        prose-code:text-[var(--color-text)] prose-code:bg-[var(--color-background)]
        prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
        prose-pre:bg-[#1a1a1a] prose-pre:text-gray-100 prose-pre:rounded-none prose-pre:my-6
        prose-img:grayscale prose-img:my-6
        prose-ul:list-disc prose-ul:pl-6 prose-ul:my-4
        prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-4
        prose-li:text-[var(--color-text-muted)] prose-li:my-1 prose-li:leading-relaxed prose-li:marker:text-[var(--color-text-muted)]
        prose-hr:border-[var(--color-border)] prose-hr:my-8
        prose-table:w-full prose-table:border-collapse prose-table:my-6
        prose-thead:border-b-2 prose-thead:border-[var(--color-border)]
        prose-th:bg-[var(--color-background)] prose-th:p-3 prose-th:text-left prose-th:font-medium prose-th:text-[var(--color-text)] prose-th:border prose-th:border-[var(--color-border)]
        prose-td:p-3 prose-td:border prose-td:border-[var(--color-border)] prose-td:text-[var(--color-text-muted)]
        prose-headings:scroll-mt-24
        [&>p+p]:mt-6
      `;

    case 'route-royal':
      return `
        prose prose-lg max-w-none
        prose-headings:font-bold prose-headings:text-gray-900
        prose-h1:text-4xl prose-h1:mb-6 prose-h1:mt-0
        prose-h2:text-3xl prose-h2:mb-4 prose-h2:mt-10 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
        prose-h3:text-2xl prose-h3:mb-3 prose-h3:mt-8
        prose-h4:text-xl prose-h4:mb-2 prose-h4:mt-6
        prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
        prose-a:text-blue-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
        prose-strong:text-gray-900 prose-strong:font-bold
        prose-em:italic
        prose-blockquote:border-l-blue-500 prose-blockquote:border-l-4
        prose-blockquote:bg-gray-50 prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:my-6
        prose-blockquote:rounded-r prose-blockquote:text-gray-700 prose-blockquote:not-italic
        prose-code:text-blue-800 prose-code:bg-blue-50 prose-code:px-1.5 prose-code:py-0.5
        prose-code:rounded prose-code:font-medium prose-code:before:content-none prose-code:after:content-none
        prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:my-6
        prose-img:rounded-lg prose-img:my-6
        prose-ul:list-disc prose-ul:pl-6 prose-ul:my-4
        prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-4
        prose-li:text-gray-700 prose-li:my-1 prose-li:leading-relaxed prose-li:marker:text-blue-500
        prose-hr:border-gray-200 prose-hr:my-8
        prose-table:w-full prose-table:border-collapse prose-table:my-6
        prose-thead:border-b-2 prose-thead:border-gray-300
        prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left prose-th:font-semibold prose-th:text-gray-900 prose-th:border prose-th:border-gray-200
        prose-td:p-3 prose-td:border prose-td:border-gray-200 prose-td:text-gray-700
        prose-headings:scroll-mt-24
        [&>p+p]:mt-6
      `;

    case 'custom':
      // No default styles, use className
      return '';

    case 'default':
    default:
      return `
        prose prose-lg max-w-none
        prose-headings:font-bold prose-headings:text-gray-900
        prose-h1:text-4xl prose-h1:mb-6 prose-h1:mt-0
        prose-h2:text-3xl prose-h2:mb-4 prose-h2:mt-10 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
        prose-h3:text-2xl prose-h3:mb-3 prose-h3:mt-8
        prose-h4:text-xl prose-h4:mb-2 prose-h4:mt-6
        prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
        prose-a:text-primary prose-a:font-medium prose-a:no-underline hover:prose-a:underline
        prose-strong:text-gray-900 prose-strong:font-bold
        prose-em:italic
        prose-blockquote:border-l-primary prose-blockquote:border-l-4
        prose-blockquote:bg-gray-50 prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:my-6
        prose-blockquote:rounded-r prose-blockquote:text-gray-700 prose-blockquote:not-italic
        prose-code:text-primary prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5
        prose-code:rounded prose-code:font-medium prose-code:before:content-none prose-code:after:content-none
        prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:my-6
        prose-img:rounded-lg prose-img:my-6
        prose-ul:list-disc prose-ul:pl-6 prose-ul:my-4
        prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-4
        prose-li:text-gray-700 prose-li:my-1 prose-li:leading-relaxed prose-li:marker:text-gray-400
        prose-hr:border-gray-200 prose-hr:my-8
        prose-table:w-full prose-table:border-collapse prose-table:my-6
        prose-thead:border-b-2 prose-thead:border-gray-300
        prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left prose-th:font-semibold prose-th:text-gray-900 prose-th:border prose-th:border-gray-200
        prose-td:p-3 prose-td:border prose-td:border-gray-200 prose-td:text-gray-700
        prose-headings:scroll-mt-24
        [&>p+p]:mt-6
      `;
  }
}

/**
 * MarkdownRenderer Component
 *
 * Renders markdown content safely with beautiful typography.
 */
export function MarkdownRenderer({
  content,
  contentFormat,
  theme = 'default',
  className,
  addHeadingIds: _addHeadingIds = true,
  style,
}: MarkdownRendererProps) {
  // Normalize contentFormat - default to MARKDOWN if undefined
  const format: ContentFormat = contentFormat ?? 'MARKDOWN';

  // Parse and sanitize content
  const html = useMemo(() => {
    if (!content) return '';

    // Parse content based on format
    const parsedHtml = parseContent(content, format);

    // Sanitize for security
    return sanitizeHTML(parsedHtml);
  }, [content, format]);

  // Get theme-specific classes
  const themeClasses = getThemeClasses(theme);

  // Return null if no content to render
  if (!content || !html) {
    return null;
  }

  // Apply paragraph spacing via CSS custom properties
  const combinedStyle = {
    ...style,
    '--paragraph-spacing': '1.5rem',
  } as React.CSSProperties;

  return (
    <>
      <style>{`
        /* Paragraph spacing */
        .markdown-content p + p {
          margin-top: var(--paragraph-spacing, 1.5rem);
        }
        .markdown-content p {
          margin-bottom: var(--paragraph-spacing, 1.5rem);
        }

        /* Table responsive overflow wrapper */
        .markdown-content .table-overflow {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          margin-top: 1.5rem;
          margin-bottom: 1.5rem;
          border-radius: 0.375rem;
        }
        .markdown-content .table-overflow table {
          margin-top: 0;
          margin-bottom: 0;
        }

        /* Nested list style variations */
        .markdown-content ul ul { list-style-type: circle; }
        .markdown-content ul ul ul { list-style-type: square; }
        .markdown-content ol ol { list-style-type: lower-alpha; }
        .markdown-content ol ol ol { list-style-type: lower-roman; }

        /* Tighter spacing for list items containing paragraphs */
        .markdown-content li > p {
          margin-top: 0.25rem;
          margin-bottom: 0.25rem;
        }
        .markdown-content li > p:first-child {
          margin-top: 0;
        }
        .markdown-content li > p:last-child {
          margin-bottom: 0;
        }

        /* Nested list spacing */
        .markdown-content li > ul,
        .markdown-content li > ol {
          margin-top: 0.375rem;
          margin-bottom: 0.375rem;
        }
      `}</style>
      <div
        className={cn('markdown-content', themeClasses, className)}
        style={combinedStyle}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
}

/**
 * Hook to extract headings from markdown content for TOC
 */
export function useMarkdownHeadings(
  content: string | undefined,
  contentFormat: ContentFormat | undefined = 'MARKDOWN'
): Array<{ id: string; text: string; level: number }> {
  const format = contentFormat ?? 'MARKDOWN';

  return useMemo(() => {
    if (!content) return [];

    // For markdown, extract headings from raw content
    if (format === 'MARKDOWN' || format === 'MDX') {
      const headingRegex = /^(#{1,6})\s+(.+)$/gm;
      const headings: Array<{ id: string; text: string; level: number }> = [];
      let match;

      while ((match = headingRegex.exec(content)) !== null) {
        const level = match[1]?.length ?? 2;
        const text = match[2] ?? '';
        if (text && level <= 3) {
          // Only h1-h3 for TOC
          const id = text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
          headings.push({ id, text, level });
        }
      }

      return headings;
    }

    // For HTML, extract from parsed content
    if (format === 'HTML') {
      const headingRegex = /<h([1-3])[^>]*>([^<]+)<\/h[1-3]>/gi;
      const headings: Array<{ id: string; text: string; level: number }> = [];
      let match;

      while ((match = headingRegex.exec(content)) !== null) {
        const level = parseInt(match[1] ?? '2', 10);
        const text = match[2] ?? '';
        if (text) {
          const id = text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
          headings.push({ id, text, level });
        }
      }

      return headings;
    }

    return [];
  }, [content, format]);
}

/**
 * Parse markdown to HTML string (for SSR or custom rendering)
 */
export function parseMarkdown(
  content: string,
  format: ContentFormat = 'MARKDOWN'
): string {
  return sanitizeHTML(parseContent(content, format));
}

export default MarkdownRenderer;
